import { cardFaceNames } from './cardFaces'

export const normalizeSearchText = (value: string): string => {
  return String(value ?? '')
    .trim()
    .replace(/\s+/g, ' ')
}

/**
 * For a pure-numeric fraction token like "14/55", generate all zero-padded
 * variants so that client-side matching handles "014/055" ↔ "14/55" etc.
 * Mirrors the backend expandSetNumberVariants utility.
 */
export const expandSetNumberVariants = (token: string): string[] => {
  const match = token.match(/^(\d+)\/(\d+)$/)
  if (!match) return [token]

  const [, rawNum, rawDenom] = match
  const strNum   = String(parseInt(rawNum, 10))
  const strDenom = String(parseInt(rawDenom, 10))

  const variants = new Set<string>()
  variants.add(token)
  variants.add(`${strNum}/${strDenom}`)
  for (const w of [2, 3, 4]) {
    variants.add(`${strNum.padStart(w, '0')}/${strDenom.padStart(w, '0')}`)
  }
  return [...variants]
}

export const tokenizeSearchText = (value: string): string[] => {
  const normalized = normalizeSearchText(value)
  if (!normalized) return []

  const prepared = normalized
    .toLowerCase()
    .replace(/&/g, ' __and__ ')
    .replace(/\band\b/g, ' __and__ ')
    .replace(/\(/g, ' __open_paren__ ')
    .replace(/\)/g, ' __close_paren__ ')

  // Preserve / and - because they are significant in set numbers (e.g. "14/55", "SM-P")
  const cleaned = prepared
    .replace(/[^\p{L}\p{N}_\/-]+/gu, ' ')
    .trim()

  if (!cleaned) return []

  const seen = new Set<string>()
  const tokens: string[] = []

  for (const rawToken of cleaned.split(' ')) {
    if (!rawToken) continue

    // Strip leading/trailing slashes and dashes; keep internal ones
    const token = rawToken.startsWith('__')
      ? rawToken
      : rawToken.replace(/^[/-]+|[/-]+$/g, '')

    if (!token || seen.has(token)) continue
    seen.add(token)
    tokens.push(token)
    if (tokens.length >= 8) break
  }

  return tokens
}

export const canSearchQuery = (value: string, minLength = 2): boolean => {
  const normalized = normalizeSearchText(value)
  if (!normalized) return false
  if (/^\d+$/.test(normalized)) return true
  return normalized.length >= minLength && tokenizeSearchText(normalized).length > 0
}

const toSearchBlob = (parts: Array<string | undefined | null>): string => {
  return parts
    .map((part) => String(part ?? '').toLowerCase())
    .join(' ')
}

export const matchesTokenizedSearch = (
  query: string,
  fields: Array<string | undefined | null>,
): boolean => {
  const tokens = tokenizeSearchText(query)
  if (!tokens.length) return true

  const blob = toSearchBlob(fields)
  return tokens.every((token) => {
    if (token === '__and__') {
      return blob.includes('&') || blob.includes(' and ')
    }
    if (token === '__open_paren__') {
      return blob.includes('(')
    }
    if (token === '__close_paren__') {
      return blob.includes(')')
    }
    // For pure-numeric fractions, check all zero-padded variants so "14/55"
    // matches a card whose set number is stored as "014/055" (and vice-versa).
    if (/^\d+\/\d+$/.test(token)) {
      return expandSetNumberVariants(token).some((v) => blob.includes(v))
    }
    return blob.includes(token)
  })
}

/**
 * Score how well a card matches the set number input box.
 * High score = strong match on collector number or set code.
 */
export const scoreSetNumberRelevance = (
  setNumberQuery: string,
  cardSetNumber?: string,
  cardSetCode?: string,
): number => {
  const normQuery = normalizeSearchText(setNumberQuery).toLowerCase()
  if (!normQuery) return 0

  const setNum = String(cardSetNumber || '').toLowerCase().trim()
  const setCode = String(cardSetCode || '').toLowerCase().trim()
  if (!setNum && !setCode) return 0

  // 1. Exact match on card set number (e.g. "#SM108" == "sm108", "#005" == "005")
  if (setNum === normQuery) return 1000

  // 2. Exact match on set code (e.g. setCode "swsh02" == "swsh02")
  if (setCode === normQuery) return 900

  // 3. Normalized number / fraction match (e.g. "14/55" ↔ "014/055", "0044" ↔ "44")
  const variants = expandSetNumberVariants(normQuery)
  if (variants.some((v) => setNum === v)) return 950

  // Bare numeric equivalence (e.g. normQuery "108" matches setNum "SM108" or "0108")
  const bareQueryMatch = normQuery.match(/\d+/)
  const bareSetNumMatch = setNum.match(/\d+/)
  if (bareQueryMatch && bareSetNumMatch) {
    const bareQ = String(parseInt(bareQueryMatch[0], 10))
    const bareS = String(parseInt(bareSetNumMatch[0], 10))
    if (bareQ === bareS) {
      if (setNum.includes(normQuery) || normQuery.includes(setNum)) return 900
      return 600
    }
  }

  // 4. Combined code + number match across any TCG (e.g. "sm108", "fb02-23", "op01-008", "p1-001")
  const combined = normQuery.match(/^([a-z]{1,8}\d{0,3})[\s\-_]*([a-z]?\d{1,5}[a-z]?(?:\/\d{1,5})?)$/i)
  if (combined) {
    const [, codePart, numberPart] = combined
    const codeMatch = setCode === codePart || setCode.startsWith(codePart) || codePart.startsWith(setCode)
    const numMatch = setNum === numberPart || setNum.includes(numberPart) || expandSetNumberVariants(numberPart).some(v => setNum.includes(v))
    if (codeMatch && numMatch) return 1000
    if (codeMatch) return 300
    if (numMatch) return 400
  }

  // 5. Prefix / contains match
  if (setNum.startsWith(normQuery)) return 500
  if (setNum.includes(normQuery)) return 350
  if (setCode.startsWith(normQuery)) return 300
  if (setCode.includes(normQuery)) return 200

  return 0
}

/**
 * Score how well a card matches the search query and set number filter.
 * Higher score = better match. Use for relevance-based sorting.
 */
export const scoreSearchRelevance = (
  query: string,
  name: string,
  hasImage?: boolean,
  setNumberQuery?: string,
  cardSetNumber?: string,
  cardSetCode?: string,
): number => {
  let score = 0

  if (setNumberQuery) {
    score += scoreSetNumberRelevance(setNumberQuery, cardSetNumber, cardSetCode)
  }

  const tokens = tokenizeSearchText(query)
  if (tokens.length > 0) {
    const nameLower = String(name || '').toLowerCase()
    const faces = cardFaceNames(nameLower)
    const nameWords = nameLower.split(/[\s\-']+/)

    for (const token of tokens) {
      if (token.startsWith('__')) continue
      if (faces.includes(token)) score += 200
      else if (nameLower.startsWith(token)) score += 100
      else if (nameWords.some((w) => w.startsWith(token))) score += 60
      else if (nameLower.includes(token)) score += 20
    }

    // Prefer shorter, more specific names
    score += Math.max(0, 30 - faces[0].length)
  }

  if (hasImage) score += 15

  return score
}
